import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
// --- Import Hooks
import useMakeRequest from "../../../hooks/useMakeRequest";
// --- Import Components
import Toggle from "../../../components/Toggle/Toggle";
import PaymentFrame from "../../../components/PaymentFrame/PaymentFrame";
import NewButton from "../../../components/UI/Button/NewButton";
import ModalWindow from "../../../components/UI/ModalWindow/ModalWindow";
// --- Import Styles
import "./SubscriptionsListItem.sass";
import "../../../components/UI/ModalWindow/ModalWindow.sass";

// --- Import images
import iconThreeDots from "../../../assets/images/icons/icon_three_dots.svg";
import { ReactComponent as TooltipQuestion } from "../../../assets/images/icons/icon_help.svg";

function convertDate(date) {
  // The date comes in seconds. Here I cheat on her in milliseconds
  const dateMs = new Date(date * 1000);
  const formatedDate = new Intl.DateTimeFormat(window.navigator.language)
    .format(dateMs)
    .replaceAll(".", "/");

  return formatedDate;
}

const PCAS_MEMBERSHIP_KEYS = ["vfp_1646", "pcappstore_member"];

function SubscriptionsListItem({ userData, product, showRenew }) {
  const [setParams, result, isLoading, hasError, error] = useMakeRequest();
  const [isSubscribed, setIsSubscribed] = useState(
    product.cancel_autobilling === 0
  );
  const [settingsIsOpened, setSettingsIsOpened] = useState(false);
  const [openPaymentFrame, setOpenPaymentFrame] = useState(false);
  const [productState, setProductState] = useState(null);
  const [showAutoRenewalConfirm, setShowAutoRenewalConfirm] = useState(false);
  const itemMenuRef = useRef(null);

  const isMembership = useMemo(
    () =>
      PCAS_MEMBERSHIP_KEYS.some((key) => product.product_key?.includes(key)),
    [product.product_key]
  );

  function getLicenseStatus(state) {
    const obj = {
      active: {
        text: "Active since ",
        color: "#32CD38",
        date: product?.activated_time,
        alternativeText: `Active until ${convertDate(
          product?.license_expire_time
        )} and Renews on ${convertDate(product?.renew_time)} for $${
          product.amount
        }`,
      },
      expired: {
        text: "Expired on ",
        color: "#E41F00",
        date: product?.license_expire_time,
      },
      "expired soon": {
        text: "Expiring on ",
        color: "#E41F00",
        date: product?.license_expire_time,
      },
      canceled: {
        text: "Cancelled on ",
        color: "#C4C4C4",
        date: product?.canceled_time,
      },
      "not activated": {
        text: "The license has not been activated",
        color: "#C4C4C4",
        date: "",
      },
    };
    if (!obj[state]) {
      return obj.active;
    }
    return obj[state];
  }

  const sendSubscriptionStatus = useCallback(
    (productData, status) => {
      setParams({
        c: "accountStore",
        a: "userLicenseAutoBillingSetState",
        p: {
          lic_id: productData.id,
          status,
        },
      });
    },
    [setParams]
  );

  const enableSubscription = useCallback(() => {
    setIsSubscribed(true);
    sendSubscriptionStatus(product, true);
  }, [product, sendSubscriptionStatus]);

  const cancelSubscription = useCallback(() => {
    setShowAutoRenewalConfirm(false);
    setIsSubscribed(false);
    sendSubscriptionStatus(product, false);
  }, [
    product,
    setParams,
    setShowAutoRenewalConfirm,
    setIsSubscribed,
    sendSubscriptionStatus,
  ]);

  const closeAutoRenewalConfirm = useCallback(() => {
    setShowAutoRenewalConfirm(false);
  }, [setShowAutoRenewalConfirm]);

  useEffect(() => {
    setProductState(getLicenseStatus(product.state.toLowerCase()));
  }, [result]);

  useEffect(() => {
    if (!settingsIsOpened) return;

    function handleClickOutside(e) {
      if (itemMenuRef.current && !itemMenuRef.current.contains(e.target)) {
        setSettingsIsOpened(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line consistent-return
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [settingsIsOpened]);

  return (
    <tr
      className={`subscriptions-list-item ${
        settingsIsOpened ? "subscriptions-list-item--active" : ""
      }`}
    >
      <td className="subscriptions-list-item__cell">
        <p className="subscriptions-list-item__text">
          <strong>{product.name_prod} </strong>
          {product.license_name}
        </p>
      </td>
      <td className="subscriptions-list-item__cell">
        <div className="subscriptions-list-item__status">
          <span
            className="subscriptions-list-item__chip"
            style={{ backgroundColor: productState?.color }}
          />
          <span className="subscriptions-list-item__text">
            {productState?.alternativeText &&
            isSubscribed &&
            product.state.toLowerCase() === "active"
              ? productState?.alternativeText
              : `${productState?.text} ${
                  productState?.date && convertDate(productState?.date)
                }`}
          </span>
        </div>
      </td>

      <td className="subscriptions-list-item__cell subscriptions-list-item__cell--actions">
        {product.state.toLowerCase() === "expired" ||
        product.state.toLowerCase() === "expired soon" ? (
          <>
            <PaymentFrame
              frameIsOpen={openPaymentFrame}
              closeFrame={() => {
                setOpenPaymentFrame(false);
              }}
              oid={product.renew.oid}
              licId={product.id}
              prodTitle={product.name_prod}
              licPeriod={product.license_name}
              amount={product.renew.amount}
              prodKey={product.product_key}
              priceType={product.renew.price_key}
              thankYou={product.renew.url_thankyou}
            />

            <NewButton
              type="button"
              onClick={() => {
                setOpenPaymentFrame(true);
              }}
              className={`btn_new--secondary subscriptions-list-item__renew-btn ${
                !showRenew ? "subscriptions-list-item__renew-btn--last" : ""
              }`}
            >
              Renew Now
            </NewButton>
          </>
        ) : (product.isCanRenew === true || product.license_dur_days < 365) &&
          showRenew ? (
          <Toggle
            checked={isSubscribed}
            labelStart="Auto-Renewal"
            onChange={() => {
              return isSubscribed
                ? setShowAutoRenewalConfirm(true)
                : enableSubscription();
            }}
            isLoading={isLoading && !result}
            error={hasError && error ? error : ""}
            tooltip={{
              Icon: TooltipQuestion,
              text: "Cancelling your subscription requires a 30-day notice period",
            }}
          />
        ) : (
          ""
        )}
        <ModalWindow
          isOpen={showAutoRenewalConfirm}
          title={
            isMembership ? (
              <p>
                Confirm Cancelling <br /> PC App Store Membership
              </p>
            ) : (
              "Are you sure you want to cancel your auto-renewing subscription?"
            )
          }
          titleClass="modal__title"
          withClose={false}
        >
          {isMembership && (
            <div className="modal__row">
              <div className="modal__message">
                Cancellation requires a 30-day notice period. By continuing, you
                agree to be charged the partial amount for your remaining days.
                Your membership ends on{" "}
                {convertDate(product.license_expire_time)} and won&apos;t be
                renewed.
              </div>
            </div>
          )}

          <div className={`modal__row ${isMembership ? "" : "row-reverse"}`}>
            <NewButton
              type="button"
              className={
                isMembership ? "btn_new--primary" : "btn_new--secondary"
              }
              onClick={closeAutoRenewalConfirm}
            >
              {isMembership ? "Keep Membership" : "No"}
            </NewButton>
            <NewButton
              type="button"
              className={
                isMembership ? "btn_new--secondary" : "btn_new--primary"
              }
              onClick={cancelSubscription}
            >
              {isMembership ? "Cancel Membership" : "Yes"}
            </NewButton>
          </div>
        </ModalWindow>

        {!showRenew &&
          product.state.toLowerCase() !== "expired" &&
          product.state.toLowerCase() !== "canceled" && (
            <div className="subscriptions-list-item__menu" ref={itemMenuRef}>
              <button
                type="button"
                aria-label="subscription-menu"
                className="subscriptions-list-item__menu-button"
                style={{
                  background: `url(${iconThreeDots}) center no-repeat`,
                }}
                onClick={() => setSettingsIsOpened(!settingsIsOpened)}
              />
              {settingsIsOpened && (
                <div className="subscriptions-list-item__menu-dropdown">
                  <a
                    href={`/refund.php?cid=${userData.cid}&licId=${product.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="subscriptions-list-item__menu-dropdown-item"
                    onClick={() => setSettingsIsOpened(false)}
                  >
                    Refund
                  </a>
                </div>
              )}
            </div>
          )}
      </td>
    </tr>
  );
}

const mapStateToProps = (state) => ({
  userData: state.userDataStore.userData,
});

const mapDispatchToProps = {};
SubscriptionsListItem.propTypes = {
  userData: PropTypes.objectOf(PropTypes.string),
  product: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.objectOf(PropTypes.any),
    ])
  ).isRequired,
  showRenew: PropTypes.bool.isRequired,
};

SubscriptionsListItem.defaultProps = {
  userData: null,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SubscriptionsListItem);
